// 🔧 verify_email.js
// ✅ Purpose: Safely verifies Firebase email action links
// ✅ Features:
// - Reads oobCode from Firebase email verification URL
// - Applies Firebase email verification code
// - Prevents React Strict Mode double execution
// - Shows status using LGV notification style
// - Signs out any active unverified Safari session after verification
// - Redirects user back to login with emailVerified message

import React, { useEffect, useRef, useState } from "react";
import { getAuth, applyActionCode } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate, useSearchParams } from "react-router-dom";
import { logger } from "../../utils/logger";
import { showStatus } from "../../utils/status_helpers";
import {
  SESSION_KEYS,
  getSessionValue,
  clearPendingVoteNavigation,
} from "../../utils/browser_storage_helpers";
import SeoHead from "../../components/seo_head";

const VerifyEmail = () => {

  const [status, setStatus] = useState({
    type: "info",
    message: "Verifying your email...",
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const verifyEmail = async () => {
      const oobCode = searchParams.get("oobCode");

      if (!oobCode) {
        showStatus(setStatus, {
          type: "error",
          message: "Invalid verification link.",
        });

        return;
      }

      const auth = getAuth();

      try {

        await applyActionCode(auth, oobCode);

        logger.log("✅ Email verification code applied successfully.", {});

        showStatus(setStatus, {
          type: "success",
          message: "Your email has been verified. Taking you back to Let's Go Vote...",
        });

        const pendingCid = getSessionValue(
          SESSION_KEYS.PENDING_VOTE_CID
        );

        const eventSlug = getSessionValue(
          SESSION_KEYS.LAST_EVENT_SLUG
        );

        if (auth.currentUser) {
          await auth.currentUser.reload();
          logger.log("📩 After reload()", {
            emailVerified: auth.currentUser.emailVerified,
          });

          // Refresh Firebase auth token
          await auth.currentUser.getIdToken(true);

          try {
            const linkPendingUser = httpsCallable(
              getFunctions(),
              "linkPendingUserToUser"
            );
            await linkPendingUser({});
          } catch (pendingLinkError) {
            // Verification succeeds independently; a later authenticated retry
            // can safely complete an invitation claim.
            logger.warn("Pending invitation linking will be retried later.", {
              code: pendingLinkError?.code || "",
            });
          }
        }

        setTimeout(() => {
          if (pendingCid && eventSlug) {
            clearPendingVoteNavigation();
            navigate(`/${eventSlug}/vote/${pendingCid}`, { replace: true });
          } else {
            navigate("/dashboard?emailVerified=1", { replace: true });
          }
        }, 1500);

      } catch (error) {
        logger.error("❌ applyActionCode error:", {
          value: error,
        });

        showStatus(setStatus, {
          type: "error",
          message:
            "Failed to verify email. This link may be expired or already used.",
        });
      }
    };

    verifyEmail();
  }, [navigate, searchParams]);

return (
  <>
    <SeoHead
      title="Verify Email | Let's Go Vote"
      description="Email verification page."
      path="/verify_email"
      noindex={true}
    />

    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">📩 Email Verification</h1>
          <p className="lead text-muted">
            Confirm your email to access the system
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        {status?.message && (
          <div
            role="alert"
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}
      </div>
    </section>
  </>
  );
};

export default VerifyEmail;
